// Import-Center: easybooking-Übernahme (Kunden-ID + Seriennummer) · Portal-/Webseiten-Scanner · Fehlerliste · Kanal-IDs
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Download, Link2, Loader2, AlertTriangle, CheckCircle2, AlertCircle, RefreshCw, Save, Globe, Hash, Trash2 } from "lucide-react";
import { PackagePanel } from "./PackagePanel";
import { FestwetterPanel, DankePanel } from "./Festwetter";
import { HerbstfestRueckblick } from "./Rueckblick";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/import`;
const STATUS = {
  ok: { label: "OK", color: "#10B981", Icon: CheckCircle2 },
  warning: { label: "Lücken", color: "#F59E0B", Icon: AlertTriangle },
  error: { label: "Fehlerhaft", color: "#F43F5E", Icon: AlertCircle },
};

export function ImportCenter() {
  const [msg, setMsg] = useState("");
  const [sources, setSources] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [errors, setErrors] = useState({ total: 0, items: [] });
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 5000); };
  const load = async () => {
    const [s, j, e] = await Promise.all([axios.get(`${API}/sources`), axios.get(`${API}/jobs`), axios.get(`${API}/errors`)]);
    setSources(s.data); setJobs(j.data); setErrors(e.data);
  };
  useEffect(() => { load(); }, []);
  const eb = sources.find((s) => s.key === "easybooking");

  return (
    <div className="space-y-5" data-testid="import-center-view">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Download size={22} className="text-eb-primary" /> Import-Center</h2>
          <p className="text-eb-muted text-sm">Bestehende Objekte übernehmen · easybooking-Schnittstelle · Portal-Scanner (Booking.com, Airbnb, Vrbo …) · Direkt-Import mit Fehlerliste</p>
        </div>
        {msg && <span className="text-emerald-400 text-xs" data-testid="import-msg">{msg}</span>}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-5">
        <div className="space-y-4">
          {eb && <EasybookingPanel eb={eb} reload={load} flash={flash} />}
          <PackagePanel flash={flash} />
          <FestwetterPanel />
          <DankePanel />
          <HerbstfestRueckblick />
          <ScannerPanel sources={sources.filter((s) => s.kind === "scanner")} reload={load} flash={flash} />
          <ErrorList errors={errors} reload={load} flash={flash} />
        </div>
        <div className="space-y-4">
          <ChannelIdPanel reload={load} flash={flash} />
          <JobList jobs={jobs} />
        </div>
      </div>
    </div>
  );
}

function EasybookingPanel({ eb, reload, flash }) {
  const [cfg, setCfg] = useState({ customer_id: "", serial_no: "", locale_id: 2, label: "" });
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const accounts = eb.accounts || [];
  const save = async () => {
    setBusy("save");
    try { const r = await axios.put(`${API}/easybooking/config`, cfg); flash(`✓ Verbunden: ${r.data.company} (${r.data.city})`); setCfg({ customer_id: "", serial_no: "", locale_id: 2, label: "" }); reload(); }
    catch (e) { flash(e.response?.data?.detail || "Verbindung fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const run = async (customerId) => {
    setBusy(customerId || "all");
    try {
      const r = await axios.post(`${API}/easybooking/run${customerId ? `?customer_id=${customerId}` : ""}`);
      setResult(r.data); flash(`✓ ${r.data.summary.total} Objekte übernommen`); reload();
    } catch (e) { flash(e.response?.data?.detail || "Import fehlgeschlagen"); }
    finally { setBusy(""); }
  };
  const remove = async (customerId) => {
    if (!window.confirm("easybooking-Konto entfernen? Bereits importierte Objekte bleiben erhalten.")) return;
    await axios.delete(`${API}/easybooking/config/${customerId}`); flash("✓ Konto entfernt"); reload();
  };
  return (
    <div className="card space-y-3" data-testid="easybooking-import-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="overline flex items-center gap-1"><Link2 size={12} /> easybooking · Schnittstelle (Kategorien, Zimmer, Preise, Bilder, IDs)</div>
        <span className="channel-pill" style={{ background: eb.connected ? "#10B98122" : "#374151", color: eb.connected ? "#10B981" : "#D1D5DB" }} data-testid="easybooking-status">
          {eb.connected ? `${accounts.length} Konto${accounts.length === 1 ? "" : "s"} verbunden` : "Nicht verbunden"}
        </span>
      </div>
      {accounts.length > 0 && (
        <div className="space-y-1" data-testid="easybooking-accounts">
          {accounts.map((a) => (
            <div key={a.customer_id} className="eb-surface rounded-md px-3 py-2 flex items-center justify-between gap-2 flex-wrap text-xs" data-testid={`eb-account-${a.customer_id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{a.label} <span className="text-eb-muted font-mono">· Kunden-ID {a.customer_id}</span></div>
                <div className="text-eb-muted truncate">{a.company}{a.city ? ` · ${a.city}` : ""}{a.website ? ` · ${a.website}` : ""} · {a.properties} Objekte importiert</div>
              </div>
              <div className="flex gap-1">
                <button className="btn !py-1" onClick={() => run(a.customer_id)} disabled={!!busy} data-testid={`eb-run-${a.customer_id}`}>
                  {busy === a.customer_id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Übernehmen
                </button>
                <button className="btn !py-1 text-rose-400" onClick={() => remove(a.customer_id)} disabled={!!busy} data-testid={`eb-remove-${a.customer_id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr_80px_auto] gap-2">
        <input placeholder="Bezeichnung (z. B. Feriensiedlung Schwalbennest)" value={cfg.label} onChange={(e) => setCfg({ ...cfg, label: e.target.value })} data-testid="eb-label" />
        <input placeholder="Kunden-ID" value={cfg.customer_id} onChange={(e) => setCfg({ ...cfg, customer_id: e.target.value })} data-testid="eb-customer-id" />
        <input placeholder="Seriennummer (z. B. 1234-5678-9012)" value={cfg.serial_no} onChange={(e) => setCfg({ ...cfg, serial_no: e.target.value })} data-testid="eb-serial-no" />
        <select value={cfg.locale_id} onChange={(e) => setCfg({ ...cfg, locale_id: parseInt(e.target.value) })} data-testid="eb-locale">
          <option value={2}>DE</option><option value={1}>EN</option><option value={4}>IT</option><option value={5}>FR</option>
        </select>
        <button className="btn" onClick={save} disabled={!!busy || !cfg.customer_id || !cfg.serial_no} data-testid="eb-save">
          {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Konto hinzufügen
        </button>
      </div>
      <div className="text-[10px] text-eb-muted">Kunden-ID & Seriennummer findest du im easybooking Zimmerplan unter „Mein EB → Hilfe & Kontakt → Info“. Mehrere easybooking-Konten (z. B. je Siedlung) sind möglich.</div>
      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn btn-primary" onClick={() => run()} disabled={!!busy || !eb.connected} data-testid="eb-run-import">
          {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Alle Konten jetzt übernehmen
        </button>
        <span className="text-xs text-eb-muted">Direkt-Import: Objekte werden sofort angelegt bzw. aktualisiert; Lücken landen in der Fehlerliste.</span>
      </div>
      {result && (
        <div className="eb-surface rounded-md p-3 space-y-1" data-testid="eb-import-result">
          <div className="text-xs font-semibold">Ergebnis: {result.summary.total} Objekte · {result.summary.ok} OK · {result.summary.warning} mit Lücken · {result.summary.error} fehlerhaft · {result.duration_ms} ms{result.accounts ? ` · ${result.accounts} Konten` : ""}</div>
          {result.items.map((i) => <ImportItemRow key={i.property_id} item={i} />)}
          {result.note && <div className="text-[10px] text-eb-muted">{result.note}</div>}
        </div>
      )}
    </div>
  );
}

function ImportItemRow({ item }) {
  const s = STATUS[item.status] || STATUS.ok;
  return (
    <div className="flex items-start gap-2 text-xs" data-testid={`import-item-${item.property_id}`}>
      <s.Icon size={13} style={{ color: s.color, marginTop: 2 }} />
      <div className="min-w-0">
        <span className="font-medium">{item.name}</span>
        {item.account && <span className="text-eb-muted"> · {item.account}</span>}
        {item.easybooking_id && <span className="font-mono text-eb-muted"> · EB-ID {item.easybooking_id}</span>}
        {item.rooms > 0 && <span className="text-eb-muted"> · {item.rooms} Zimmer</span>}
        <span className="text-eb-muted"> · {item.updated ? "aktualisiert" : "neu"}</span>
        {[...(item.errors || []), ...(item.warnings || [])].length > 0 && (
          <div className="text-[11px]" style={{ color: s.color }}>{[...(item.errors || []), ...(item.warnings || [])].join(" · ")}</div>
        )}
      </div>
    </div>
  );
}

function ScannerPanel({ sources, reload, flash }) {
  const [url, setUrl] = useState("");
  const [useAi, setUseAi] = useState(true);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const scan = async () => {
    setBusy(true); setRes(null);
    try { const r = await axios.post(`${API}/scan`, { url, use_ai: useAi }); setRes(r.data); flash(r.data.updated ? "✓ Objekt aktualisiert" : "✓ Objekt angelegt"); reload(); }
    catch (e) { flash(e.response?.data?.detail || "Scan fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const p = res?.property;
  const s = res ? STATUS[res.import_status] || STATUS.ok : null;
  return (
    <div className="card space-y-3" data-testid="scanner-panel">
      <div className="overline flex items-center gap-1"><Globe size={12} /> Portal- & Webseiten-Scanner (Schema.org / OpenGraph + KI-Auswertung)</div>
      <div className="flex flex-wrap gap-1">
        {sources.map((s) => <span key={s.key} className="channel-pill bg-white/5 text-eb-muted" title={s.desc} data-testid={`scanner-source-${s.key}`}>{s.label} <span className="opacity-60 font-mono">{s.pattern}</span></span>)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
        <input placeholder="https://www.booking.com/hotel/de/… · airbnb.de/rooms/… · eigene Website" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="scan-url" />
        <label className="flex items-center gap-1 text-xs text-eb-muted px-2"><input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} data-testid="scan-use-ai" /> KI-Auswertung</label>
        <button className="btn btn-primary" onClick={scan} disabled={busy || !url} data-testid="scan-run">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Scannen & importieren
        </button>
      </div>
      {res && p && (
        <div className="eb-surface rounded-md p-3 grid grid-cols-1 md:grid-cols-[120px_1fr] gap-3" data-testid="scan-result">
          <div className="aspect-square rounded overflow-hidden bg-black/30">
            {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <div className="text-[10px] text-eb-muted p-2">kein Bild</div>}
          </div>
          <div className="space-y-1 text-xs min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">{p.name}</span>
              <span className="channel-pill" style={{ background: `${s.color}22`, color: s.color }} data-testid="scan-status">{s.label}</span>
              <span className="channel-pill bg-white/5 text-eb-muted">{res.source}{res.portal_id ? ` · ID ${res.portal_id}` : ""}</span>
              {res.ai_used && <span className="channel-pill bg-white/5 text-eb-muted">KI</span>}
            </div>
            <div className="text-eb-muted">{p.location || "—"} · {p.type} · {p.sleeps || "?"} Pers. · {p.bedrooms || "?"} SZ · {p.bathrooms || "?"} Bad · ab {p.base_price_eur ? `${p.base_price_eur} €` : "? €"} · {p.images?.length || 0} Bilder</div>
            {p.description_de && <div className="text-eb-muted line-clamp-2">{p.description_de}</div>}
            {p.amenities?.length > 0 && <div className="text-[10px] text-eb-muted truncate">{p.amenities.slice(0, 10).join(" · ")}</div>}
            {res.errors?.length > 0 && <div className="text-rose-400">{res.errors.join(" · ")}</div>}
            {res.warnings?.length > 0 && <div className="text-amber-300">{res.warnings.join(" · ")}</div>}
            <div className="text-[10px] text-eb-muted">Strukturierte Felder: {res.structured_fields?.join(", ") || "keine"} · {res.duration_ms} ms</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorList({ errors, reload, flash }) {
  const [edit, setEdit] = useState(null);
  return (
    <div className="card space-y-2" data-testid="import-error-list">
      <div className="flex items-center justify-between">
        <div className="overline flex items-center gap-1"><AlertTriangle size={12} /> Fehlerliste importierter Objekte</div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-rose-400" data-testid="error-count">{errors.errors || 0} fehlerhaft</span>
          <span className="text-amber-300" data-testid="warning-count">{errors.warnings || 0} mit Lücken</span>
          <button className="btn !py-1" onClick={reload} data-testid="error-list-refresh"><RefreshCw size={12} /></button>
        </div>
      </div>
      {errors.items.length === 0 && <div className="text-xs text-eb-muted">Keine fehlerhaften Importe. 🎉</div>}
      {errors.items.map((it) => {
        const s = STATUS[it.import_status] || STATUS.ok;
        return (
          <div key={it.id} className="eb-surface rounded-md p-2 text-xs" data-testid={`error-item-${it.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <s.Icon size={13} style={{ color: s.color }} />
                <span className="font-medium truncate">{it.name || "(ohne Name)"}</span>
                <span className="channel-pill bg-white/5 text-eb-muted">{it.source}</span>
                {Object.entries(it.channel_ids || {}).map(([k, v]) => <span key={k} className="channel-pill bg-white/5 text-eb-muted font-mono">{k}:{v}</span>)}
              </div>
              <button className="btn !py-1" onClick={() => setEdit(edit?.id === it.id ? null : it)} data-testid={`error-fix-${it.id}`}>Korrigieren</button>
            </div>
            <div className="mt-1" style={{ color: s.color }}>{[...(it.import_errors || []), ...(it.import_warnings || [])].join(" · ")}</div>
            {edit?.id === it.id && <FixForm item={it} onDone={() => { setEdit(null); reload(); }} flash={flash} />}
          </div>
        );
      })}
    </div>
  );
}

function FixForm({ item, onDone, flash }) {
  const [f, setF] = useState({ location: item.location || "", sleeps: item.sleeps || "", bedrooms: item.bedrooms || "", bathrooms: item.bathrooms || "",
    base_price_eur: item.base_price_eur || "", booking_com: item.channel_ids?.booking_com || "", airbnb: item.channel_ids?.airbnb || "", easybooking: item.channel_ids?.easybooking || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const body = { channel_ids: { booking_com: f.booking_com, airbnb: f.airbnb, easybooking: f.easybooking } };
    ["location"].forEach((k) => { if (f[k]) body[k] = f[k]; });
    ["sleeps", "bedrooms", "bathrooms"].forEach((k) => { if (f[k] !== "") body[k] = parseInt(f[k]); });
    if (f.base_price_eur !== "") body.base_price_eur = parseFloat(f.base_price_eur);
    try { const r = await axios.put(`${API}/properties/${item.id}`, body); flash(`✓ Gespeichert · Status: ${STATUS[r.data.import_status].label}`); onDone(); }
    catch (e) { flash(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  const inp = (k, ph, type = "text") => <input type={type} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`fix-${k}`} />;
  return (
    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-1" data-testid={`fix-form-${item.id}`}>
      {inp("location", "Ort")}{inp("sleeps", "Personen", "number")}{inp("bedrooms", "Schlafzimmer", "number")}{inp("bathrooms", "Bäder", "number")}
      {inp("base_price_eur", "Grundpreis €", "number")}{inp("easybooking", "easybooking-ID")}{inp("booking_com", "Booking.com-ID")}{inp("airbnb", "Airbnb-ID")}
      <button className="btn btn-primary col-span-2 md:col-span-4 justify-center" onClick={save} disabled={busy} data-testid="fix-save">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern & neu prüfen
      </button>
    </div>
  );
}

function ChannelIdPanel({ reload, flash }) {
  const [data, setData] = useState({ channels: [], items: [] });
  const [edit, setEdit] = useState({});
  const load = async () => setData((await axios.get(`${API}/channel-ids`)).data);
  useEffect(() => { load(); }, []);
  const save = async (pid) => {
    try { await axios.put(`${API}/properties/${pid}`, { channel_ids: edit[pid] }); flash("✓ Kanal-IDs gespeichert"); setEdit({ ...edit, [pid]: undefined }); load(); reload(); }
    catch (e) { flash(e.response?.data?.detail || "Fehler"); }
  };
  const short = { easybooking: "EB", booking_com: "B.com", airbnb: "Airbnb", vrbo: "Vrbo", hometogo: "HTG", expedia: "Exp", traum_fewo: "TFW" };
  return (
    <div className="card space-y-2" data-testid="channel-id-panel">
      <div className="overline flex items-center gap-1"><Hash size={12} /> Kanal-IDs je Objekt (Channel Manager)</div>
      <div className="max-h-[50vh] overflow-y-auto space-y-2">
        {data.items.map((p) => {
          const cur = edit[p.id] || p.channel_ids || {};
          const dirty = !!edit[p.id];
          return (
            <div key={p.id} className="eb-surface rounded-md p-2 text-xs" data-testid={`channel-ids-${p.id}`}>
              <div className="font-medium truncate mb-1">{p.name} {p.easybooking_rooms?.length > 0 && <span className="text-eb-muted">· {p.easybooking_rooms.length} Zimmer-IDs</span>}</div>
              <div className="grid grid-cols-2 gap-1">
                {data.channels.map((c) => (
                  <input key={c} placeholder={short[c] || c} value={cur[c] || ""} title={c} className="!text-[11px] !py-1"
                    onChange={(e) => setEdit({ ...edit, [p.id]: { ...cur, [c]: e.target.value } })} data-testid={`cid-${p.id}-${c}`} />
                ))}
              </div>
              {dirty && <button className="btn !py-1 mt-1 w-full justify-center" onClick={() => save(p.id)} data-testid={`cid-save-${p.id}`}><Save size={12} /> Speichern</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function JobList({ jobs }) {
  return (
    <div className="card space-y-2" data-testid="import-job-list">
      <div className="overline">Import-Verlauf</div>
      {jobs.length === 0 && <div className="text-xs text-eb-muted">Noch keine Importe.</div>}
      <div className="max-h-[40vh] overflow-y-auto space-y-1">
        {jobs.map((j) => (
          <div key={j.id} className="flex items-start justify-between gap-2 text-xs border-b border-white/5 pb-1" data-testid={`job-${j.id}`}>
            <div className="min-w-0">
              <div className="font-medium truncate">{j.source} · {j.name || j.company || j.url || ""}</div>
              <div className="text-eb-muted">{(j.created_at || "").slice(0, 16).replace("T", " ")} · {j.summary ? `${j.summary.total} Objekte (${j.summary.error} Fehler)` : j.import_status || j.status}</div>
            </div>
            <span className="channel-pill" style={{ background: j.status === "failed" ? "#F43F5E22" : "#10B98122", color: j.status === "failed" ? "#F43F5E" : "#10B981" }}>{j.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
